import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';

export const authConfig: AuthConfig = {
  issuer: environment.issuer,
  clientId: 'web',
  userinfoEndpoint: `${environment.issuer}/userinfo`,
  redirectUri: window.location.origin + '/callback',
  postLogoutRedirectUri: window.location.origin + '/login',
  responseType: 'code',
  scope: 'openid profile admin:read admin:write',
  requireHttps: environment.isProduction,
  showDebugInformation: true,
  disablePKCE: false,
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private redirectUrl = '/microsites';

  constructor(
    public oauthService: OAuthService,
    private router: Router,
  ) {
    this.configureOAuthService();
  }

  private configureOAuthService() {
    this.oauthService.setStorage(localStorage);
    this.oauthService.configure(authConfig);
    this.oauthService.setupAutomaticSilentRefresh();
  }

  async login() {
    await this.oauthService.loadDiscoveryDocument();
    const redirectUrl = this.getRedirectUrl() || '/microsites';
    const state = this.encodeState(redirectUrl);
    this.oauthService.initCodeFlow(state, {
      redirectUri: authConfig.redirectUri,
      scopes: authConfig.scope,
    });
  }

  private encodeState(redirectUrl: string): string {
    try {
      return btoa(
        JSON.stringify({
          redirectUrl,
          nonce: this.generateNonce(),
          timestamp: Date.now(),
        }),
      );
    } catch {
      return '';
    }
  }

  private generateNonce(length = 32): string {
    const charset =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    return Array.from(crypto.getRandomValues(new Uint8Array(length)))
      .map((x) => charset[x % charset.length])
      .join('');
  }

  setRedirectUrl(url: string): void {
    this.redirectUrl = url;
  }

  getRedirectUrl(): string {
    return this.redirectUrl;
  }

  handlePostLogin() {
    try {
      const state = this.oauthService.state;
      let redirectUrl = '/microsites';
      if (state) {
        const parsed = this.decodeState(state);
        redirectUrl = this.sanitizeUrl(parsed.redirectUrl);
      }
      this.router.navigateByUrl(redirectUrl);
    } catch {
      this.router.navigateByUrl('/microsites');
    }
  }

  private sanitizeUrl(url: string): string {
    if (url.startsWith('/') && !url.startsWith('//')) {
      return url;
    }
    return '/microsites';
  }

  logout(): void {
    this.oauthService.logOut();
    this.router.navigate(['/login']);
  }

  getToken(): string {
    return this.oauthService.getAccessToken();
  }

  isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  getProfile() {
    return this.oauthService.getIdentityClaims();
  }

  private decodeState(encodedState: string): any {
    try {
      const urlDecoded = decodeURIComponent(encodedState);
      let base64 = urlDecoded.replace(/-/g, '+').replace(/_/g, '/');
      while (base64.length % 4) {
        base64 += '=';
      }
      const json = atob(base64);
      return JSON.parse(json);
    } catch {
      throw new Error('Invalid state parameter');
    }
  }
}